// Main entry point para Worktime Client Vue 3
// Mantiene Shadow DOM y comunicación con GWT

import moment from "moment";
import { createPinia } from "pinia";
import { v4 as uuidv4 } from "uuid";
import { createApp } from "vue";
import App from "./App.vue";
import { i18n } from "./plugins/i18n";
import { registerPrimeVue } from "./plugins/primevue";
import { useWorktimeStore } from "./stores/worktime";
import "./styles/main.css";

// ============================================
// CONFIGURACIÓN DE SHADOW DOM
// ============================================

const treeHead = document.querySelector(".vue-wrapper-container");
if (!treeHead) {
  console.error("No se encontró el contenedor .vue-wrapper-container");
  throw new Error("Vue wrapper container not found");
}

const parent = document.createElement("div");
const holder = document.createElement("div");
const shadow = treeHead.attachShadow({ mode: "closed" });

// ============================================
// MONKEY PATCH querySelector PARA SHADOW DOM
// ============================================

const { querySelector: originalQuerySelector } = document;
document.querySelector = function (selector: string) {
  if (selector === "[data-app]") {
    const appInShadow = shadow.querySelector("[data-app]");
    return appInShadow || shadow;
  }
  return originalQuerySelector.call(this, selector);
};

// ============================================
// CARGAR ESTILOS EN SHADOW DOM
// ============================================

function loadStyleInShadow(href: string): void {
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  shadow.appendChild(link);
}

function captureViteStyles(): void {
  setTimeout(() => {
    const styles = document.head.querySelectorAll("style");
    let captured = 0;

    styles.forEach((style) => {
      if (cloneStyleToShadow(style)) {
        captured++;
      }
    });

    if (!import.meta.env.PROD) {
      console.log(`📦 ${captured} estilos de Vite capturados inicialmente`);
    }
  }, 100);
}

// Estilos de terceros desde CDN
loadStyleInShadow("https://fonts.googleapis.com/css?family=Material+Icons");
loadStyleInShadow("https://fonts.cdnfonts.com/css/google-sans");
loadStyleInShadow("https://cdn.form.io/formiojs/formio.full.min.css");
loadStyleInShadow(
  "https://stackpath.bootstrapcdn.com/font-awesome/4.7.0/css/font-awesome.min.css"
);

// Estilos locales (GWT)
loadStyleInShadow("css/formio.css");
loadStyleInShadow("css/worktime-vue.css");

// ============================================
// CREAR APLICACIÓN VUE 3
// ============================================

const pinia = createPinia();
const app = createApp(App);

// Registrar plugins
app.use(pinia);
app.use(i18n);
registerPrimeVue(app);

// ============================================
// MONTAR EN SHADOW DOM
// ============================================

window.$vueWrapperParent = parent;
shadow.appendChild(holder);

// Montar la aplicación
app.mount(holder);

// IMPORTANTE: Capturar estilos de Vite DESPUÉS de montar la app
// Esto asegura que main.css y todos sus @imports estén disponibles
setTimeout(() => {
  captureViteStyles();
  // Capturar estilos específicos de PrimeVue después
  setTimeout(() => {
    fixHeadStyles();
  }, 500);
}, 100);

// ============================================
// CONFIGURAR GLOBALS PARA GWT
// ============================================

window.$vue = app;
window.$vueWrapper = treeHead as HTMLElement; // ✅ treeHead
window.$vueWrapper.style.display = "none";

window.$store = pinia;
window.$i18n = i18n;

// ============================================
// INICIALIZAR UUID DEL DISPOSITIVO
// ============================================

if (!window.localStorage.getItem("worktime-device-uuid")) {
  const uuid = uuidv4().toString().toUpperCase().substring(4, 18);
  window.localStorage.setItem("worktime-device-uuid", uuid);
}

// Configurar UUID en el store
const worktimeStore = useWorktimeStore();
const deviceUuid = window.localStorage.getItem("worktime-device-uuid");
if (deviceUuid) {
  worktimeStore.setUuid(deviceUuid);
}

// ============================================
// CONFIGURAR LOCALE DE MOMENT
// ============================================

const navigatorLocale = navigator.language || "en";
moment.locale(navigatorLocale);

// ============================================
// FIX PARA ESTILOS DINÁMICOS EN SHADOW DOM
// ============================================

// Set para trackear estilos ya capturados y evitar duplicados
const capturedStyles = new Set<string>();

/**
 * Genera un ID único para un elemento de estilo
 */
function getStyleId(element: HTMLStyleElement | HTMLLinkElement): string {
  if (element.nodeName === "STYLE") {
    const styleEl = element as HTMLStyleElement;
    // Usar hash del contenido como ID único
    const content = styleEl.textContent || "";
    return `style-${content.substring(0, 100)}`; // Primeros 100 chars como ID
  } else {
    const linkEl = element as HTMLLinkElement;
    return `link-${linkEl.href}`;
  }
}

/**
 * Clona un estilo al Shadow DOM solo si no existe ya
 */
function cloneStyleToShadow(
  element: HTMLStyleElement | HTMLLinkElement
): boolean {
  const styleId = getStyleId(element);

  // Verificar si ya fue capturado
  if (capturedStyles.has(styleId)) {
    return false; // Ya existe, no clonar
  }

  // Clonar y añadir al Shadow DOM
  const cloned = element.cloneNode(true) as HTMLElement;
  cloned.setAttribute("data-shadow-style-id", styleId);
  shadow.appendChild(cloned);

  // Marcar como capturado
  capturedStyles.add(styleId);

  return true; // Clonado exitosamente
}

// Observer para capturar estilos que se añaden dinámicamente
const styleObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeName === "STYLE") {
        const styleElement = node as HTMLStyleElement;
        cloneStyleToShadow(styleElement);
      }
      if (node.nodeName === "LINK") {
        const linkElement = node as HTMLLinkElement;
        if (linkElement.rel === "stylesheet") {
          cloneStyleToShadow(linkElement);
        }
      }
    });
  });
});

// Observar cambios en el <head> para capturar estilos dinámicos
styleObserver.observe(document.head, {
  childList: true,
  subtree: true,
});

// ============================================
// FUNCIONES AUXILIARES PARA CAPTURA DE ESTILOS
// ============================================

function fixHeadStyles(): void {
  fixStyle("primevue");
  fixStyle("primeflex");
  fixStyle("primeicons");
  // Capturar TODOS los estilos restantes
  captureAllStyles();
}

function fixStyle(stylePrefix: string): void {
  const styles = document.getElementsByTagName("style");
  let captured = 0;

  for (let i = styles.length - 1; i >= 0; i--) {
    if (styles[i].innerText.indexOf(stylePrefix) >= 0) {
      if (cloneStyleToShadow(styles[i])) {
        captured++;
      }
    }
  }

  if (captured > 0 && !import.meta.env.PROD) {
    console.log(`✅ ${captured} estilos '${stylePrefix}' capturados`);
  }
}

/**
 * Captura TODOS los estilos que aún no estén en el Shadow DOM
 */
function captureAllStyles(): void {
  const allStyles = document.querySelectorAll("style, link[rel='stylesheet']");

  allStyles.forEach((styleOrLink) => {
    cloneStyleToShadow(styleOrLink as HTMLStyleElement | HTMLLinkElement);
  });

  if (!import.meta.env.PROD) {
    console.log(`✅ Total de estilos en Shadow DOM: ${capturedStyles.size}`);
  }
}

// ============================================
// CONFIGURAR LOGGER GLOBAL (Reemplaza VueLogger)
// ============================================

const isProduction = import.meta.env.PROD;

// Logger simple para reemplazar VueLogger de Vue 2
window.$log = {
  debug: (...args: unknown[]) => {
    if (!isProduction) console.log("[DEBUG]", ...args);
  },
  info: (...args: unknown[]) => {
    console.info("[INFO]", ...args);
  },
  warn: (...args: unknown[]) => {
    console.warn("[WARN]", ...args);
  },
  error: (...args: unknown[]) => {
    console.error("[ERROR]", ...args);
  },
};

// ============================================
// GOOGLE MAPS (Opcional - Descomentar si se usa)
// ============================================

/*
// Para Vue 3, usar @fawmi/vue-google-maps en lugar de vuejs-google-maps
// npm install @fawmi/vue-google-maps

import VueGoogleMaps from '@fawmi/vue-google-maps'

app.use(VueGoogleMaps, {
  load: {
    key: 'AIzaSyC5MifXnsUxeSBYtxX1HUV1QCUFoOtC9H4',
    libraries: ['places', 'geometry']
  },
})
*/

// ============================================
// LOG DE INICIALIZACIÓN
// ============================================

window.$log.debug("✅ Worktime Client Vue 3 inicializado correctamente");
window.$log.debug("📦 Shadow DOM configurado");
window.$log.debug("🔗 Comunicación GWT lista");
window.$log.debug(`🌐 Locale: ${navigatorLocale}`);
window.$log.debug(`🆔 Device UUID: ${deviceUuid}`);
window.$log.debug("🛠️ Modo:", isProduction ? "Producción" : "Desarrollo");
